import { UppFloorPlanTable } from '../../floor-plan-element';
import type { FloorPlanAreaSourceDrawTableItem } from '../floor-plan-wire-types';
import type { UppKonvaShape } from '../floor-plan-wire-types';
import { elementOptsFromLabels } from './shape-bridge-helpers';
import type { FloorPlanShapeBridge, SourceToWireInput } from './shape-bridge-types';

function adapterRowLabels(row: UppKonvaShape): {
  label?: string;
  sublabel?: string;
  zIndex?: number;
  custom?: unknown;
} {
  const o: { label?: string; sublabel?: string; zIndex?: number; custom?: unknown } = {};
  if ('label' in row && row.label !== undefined) {
    o.label = row.label;
  }
  if ('sublabel' in row && row.sublabel !== undefined) {
    o.sublabel = row.sublabel;
  }
  if ('zIndex' in row && row.zIndex !== undefined) {
    o.zIndex = row.zIndex;
  }
  if (Object.prototype.hasOwnProperty.call(row, 'custom')) {
    o.custom = (row as { custom?: unknown }).custom;
  }
  return o;
}

export const tableShapeBridge: FloorPlanShapeBridge = {
  kind: 'TABLE',
  wireType: 'TABLE',
  sourceToWire(i: SourceToWireInput): UppKonvaShape {
    const shapeRaw = String(i.d['shape'] ?? 'SQUARE').toUpperCase();
    const tableShape: 'SQUARE' | 'CIRCLE' = shapeRaw === 'CIRCLE' ? 'CIRCLE' : 'SQUARE';
    const chairs =
      typeof i.d['chairs'] === 'number' && Number.isFinite(i.d['chairs']) ? (i.d['chairs'] as number) : 4;
    const dinersRaw = i.d['diners'];
    const diners =
      typeof dinersRaw === 'number' && Number.isFinite(dinersRaw) ? Math.max(0, Math.trunc(dinersRaw as number)) : undefined;
    const t: FloorPlanAreaSourceDrawTableItem = {
      type: 'TABLE',
      x: i.x,
      y: i.y,
      width: i.width,
      height: i.height,
      rotation: i.rotation,
      chairs,
      shape: tableShape,
      ...(diners !== undefined ? { diners } : {}),
      ...(i.id.uuid ? { uuid: i.id.uuid } : {}),
      ...i.lb,
    };
    return { ...t, ...(!i.id.uuid ? { id: i.id.id } : {}) } as UppKonvaShape;
  },
  wireToSource(row: UppKonvaShape) {
    const r = row as FloorPlanAreaSourceDrawTableItem;
    const uuid = r.uuid !== undefined && r.uuid.trim() !== '' ? r.uuid.trim() : '';
    const legacyId = 'id' in row && typeof (row as { id?: unknown }).id === 'string' ? String((row as { id: string }).id) : '';
    const id = uuid || legacyId || 'shape-TABLE';
    const base = {
      id,
      x: r.x,
      y: r.y,
      width: r.width,
      height: r.height,
      rotation: r.rotation,
      ...adapterRowLabels(row),
    };
    const rs = r.shape;
    const rsU = typeof rs === 'string' ? rs.toUpperCase() : '';
    const out: Record<string, unknown> = {
      shape: rsU === 'CIRCLE' ? 'CIRCLE' : 'SQUARE',
      chairs: r.chairs ?? 4,
    };
    if (typeof r.diners === 'number' && Number.isFinite(r.diners)) {
      out['diners'] = Math.max(0, Math.trunc(r.diners));
    }
    return {
      ...base,
      kind: 'TABLE',
      data: out,
    };
  },
  wireToElement(row: UppKonvaShape, uuid: string) {
    const t = row as FloorPlanAreaSourceDrawTableItem;
    const tableShape = t.shape === 'CIRCLE' ? 'ELLIPSE' : 'RECT';
    const el = new UppFloorPlanTable(
      uuid,
      t.x,
      t.y,
      t.width,
      t.height,
      t.rotation,
      t.chairs ?? 4,
      tableShape,
      elementOptsFromLabels(t, true),
    );
    el.zIndex = t.zIndex ?? 0;
    if (typeof t.diners === 'number' && Number.isFinite(t.diners)) {
      el.diners = Math.max(0, Math.trunc(t.diners));
    }
    return el;
  },
};
